import { isTimeframeDisabled } from ".";
import {
    Timeframe,
    TimeOption,
    VACANCY_START_AVAILABILITY,
} from "../../../models/vacancy";

// Utility: Convert HH:mm string (e.g. "14:30") into milliseconds
const parseTime = (t: string): number => {
  const [h, m] = t.split(":").map(Number);
  return ((h ?? 0) * 60 + (m ?? 0)) * 60 * 1000;
};

// Helper function to get datetime in milliseconds, handling null dates
const getDateTime = (date: Date | null, time: string): number => {
  if (!date || !time || !time.includes(":")) return 0;
  const d = new Date(date);
  const [h, m] = time.split(":").map(Number);
  d.setHours(h, m, 0, 0);
  return d.getTime();
};

const parseTimeForOverlap = (timeStr: string): [number, number] => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return [hours, minutes];
};

// Convert a (start_time, end_time) into an array of time ranges, supporting spans over midnight
const getTimeRange = (start: string, end: string): [number, number][] => {
  const [startH, startM] = parseTimeForOverlap(start);
  const [endH, endM] = parseTimeForOverlap(end);

  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  if (startMinutes < endMinutes) {
    // Same-day time range
    return [[startMinutes, endMinutes]];
  } else {
    // Overnight split: e.g. 19:00 to 05:00 => [19:00 to 24:00] and [00:00 to 05:00]
    return [
      [startMinutes, 1440], // until midnight
      [0, endMinutes], // next day
    ];
  }
};

// Check if two time ranges (in minutes) overlap
const rangesOverlap = (
  rangesA: [number, number][],
  rangesB: [number, number][]
) => {
  for (const [startA, endA] of rangesA) {
    for (const [startB, endB] of rangesB) {
      if (startA < endB && startB < endA) {
        return true;
      }
    }
  }
  return false;
};

const getDateTimeForOverlap = (
  date: string | Date | null,
  time: string
): Date | null => {
  if (!date || !time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  const base = new Date(date);
  base.setHours(hours, minutes, 0, 0);
  return base;
};

const expandTimeframeToDaySlots = (tf: Timeframe): [Date, Date][] => {
  const dates: [Date, Date][] = [];
  if (!tf.date?.[0] || !tf.date?.[1] || !tf.start_time || !tf.end_time)
    return [];

  const startDate = new Date(tf.date[0]);
  const endDate = new Date(tf.date[1]);

  const isOvernight = tf.end_time <= tf.start_time;

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const currentDay = new Date(d);
    const slotStart = getDateTimeForOverlap(currentDay, tf.start_time)!;
    const slotEnd = getDateTimeForOverlap(
      isOvernight
        ? new Date(currentDay.getTime() + 24 * 60 * 60 * 1000)
        : currentDay,
      tf.end_time
    )!;

    dates.push([slotStart, slotEnd]);
  }

  return dates;
};

// Utility: Check if two timeframes overlap
const isOverlapping = (a: Timeframe, b: Timeframe): boolean => {
  const aSlots = expandTimeframeToDaySlots(a);
  const bSlots = expandTimeframeToDaySlots(b);

  for (const [aStart, aEnd] of aSlots) {
    for (const [bStart, bEnd] of bSlots) {
      if (aStart < bEnd && bStart < aEnd) {
        return true;
      }
    }
  }
  return false;
};

// Filter out past start times if selected start date is today
// Filters out start time options based on the selected date and availability type
export const filterStartTimeOptions = (
  times: TimeOption[],
  startDate?: Date | null,
  today: Date = new Date(),
  isFutureDate: boolean = false
): TimeOption[] => {
  // If no start date is selected, return all options
  if (!startDate) return times;

  const isToday = startDate.toDateString() === today.toDateString();

  // Create a reference for "tomorrow"
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const isTomorrow = startDate.toDateString() === tomorrow.toDateString();

  return times.filter((t) => {
    if (t.value === "23:59") return false; // remove 11:59PM

    const [h, m] = t.value.split(":").map(Number);
    const timeAsDate = new Date(startDate);
    timeAsDate.setHours(h, m, 0, 0);

    // If it's the first index of FUTURE_DATE and startDate is tomorrow,
    // filter out time options that are not strictly after "tomorrow"
    if (isFutureDate && isTomorrow) {
      return timeAsDate > tomorrow;
    }

    // If the selected start date is today, only allow future time options
    if (isToday) {
      return timeAsDate > today;
    }

    // In all other cases (future dates beyond tomorrow), allow all options
    return true;
  });
};

/**
 * Filters the end time options based on the following rules:
 * - If end date is today, disallow any end times that are in the past.
 * - If start and end dates are the same, disallow end times that are less than or equal to the start time.
 * - Ensure "23:59" (11:59PM) is included if it satisfies the above rules and is not already in the list.
 */
export const filterEndTimeOptions = (
  times: TimeOption[],
  startDate?: Date | null,
  endDate?: Date | null,
  start_time?: string
): TimeOption[] => {
  const now = new Date();
  const filtered = times.filter((t) => {
    const [h, m] = t.value.split(":").map(Number);
    const endTimeAsDate = new Date(endDate || new Date());
    endTimeAsDate.setHours(h, m, 0, 0);

    const isEndToday = endDate?.toDateString() === now.toDateString();
    const isSameDay =
      startDate &&
      endDate &&
      startDate.toDateString() === endDate.toDateString();

    // Disallow past end time if today
    if (isEndToday && endTimeAsDate <= now) return false;

    // Disallow end_time <= start_time if same day
    if (isSameDay && start_time) {
      const [sh, sm] = start_time.split(":").map(Number);
      const startTimeAsDate = new Date(startDate);
      startTimeAsDate.setHours(sh, sm, 0, 0);
      return endTimeAsDate > startTimeAsDate;
    }

    return true;
  });

  // Ensure 23:59 is included if valid
  const endOfDay = new Date(endDate || new Date());
  endOfDay.setHours(23, 59, 0, 0);

  const isEndToday = endDate?.toDateString() === now.toDateString();
  const isSameDay =
    startDate && endDate && startDate.toDateString() === endDate.toDateString();

  let allowAddEndOfDay = true;

  if (isEndToday && endOfDay <= now) {
    allowAddEndOfDay = false;
  }

  if (isSameDay && start_time) {
    const [sh, sm] = start_time.split(":").map(Number);
    const startTimeAsDate = new Date(startDate);
    startTimeAsDate.setHours(sh, sm, 0, 0);
    if (endOfDay <= startTimeAsDate) {
      allowAddEndOfDay = false;
    }
  }

  // if (allowAddEndOfDay && !filtered.some((t) => t.value === "00:00")) {
  //   filtered.push({ value: "00:00", label: "12:00AM" });
  // }

  return filtered;
};

// Main timeframe validator - ensures all timeframes meet the following rules:
// - All required fields (start and end) must be present.
// - Each timeframe must represent a valid future time range (if creating a vacancy).
// - Timeframes must not overlap with each other.
// - When editing, past timeframes are ignored (assumed read-only).
// - The first timeframe must follow specific rules based on `VACANCY_START_AVAILABILITY`:
//     - If `NOW`: start time must be at or after the vacancy's `created_at` (or now if creating).
//     - If `WITHIN_24_HOURS`: start time must be within 24 hours from `created_at` (or now).
//     - If `FUTURE_DATE`: start time must be at least 24 hours after `created_at` (or now).
export const areTimeframesValid = (
  timeframes: Timeframe[],
  startAvailability: VACANCY_START_AVAILABILITY,
  isEdit: boolean = false,
  created_at: string | null = null
): { valid: boolean; index?: number; error?: string } => {
  const now = isEdit && created_at ? new Date(created_at) : new Date();
  const nowPlus24 = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const isNow = startAvailability === VACANCY_START_AVAILABILITY.NOW;
  const isWithin24 =
    startAvailability === VACANCY_START_AVAILABILITY.WITHIN_24_HOURS;
  const isFutureDate =
    startAvailability === VACANCY_START_AVAILABILITY.FUTURE_DATE;

  for (let i = 0; i < timeframes.length; i++) {
    const tf = timeframes[i];

    if (!tf.date?.[0] || !tf.date?.[1] || !tf.start_time || !tf.end_time) {
      return { valid: true }; // Allow Yup to handle field-level validation
    }

    const startDateTime = getDateTime(tf.date[0], tf.start_time);
    const endDateTime = getDateTime(tf.date[1], tf.end_time);

    // if (tf.date[0] > tf.date[1]) { // Don't think this validation is needed
    //   return {
    //     valid: false,
    //     index: i,
    //     error: "Start date cannot be after end date",
    //   };
    // }

    const startTimeMs = parseTime(tf.start_time);
    const endTimeMs = parseTime(tf.end_time);
    if (
      tf.date[0].getTime() === tf.date[1].getTime() &&
      startTimeMs >= endTimeMs
    ) {
      return {
        valid: false,
        index: i,
        error: "Start time must be before end time on the same day",
      };
    }

    // If editing, skip validation for past timeframes (allow them to be read-only)
    if (isEdit && isTimeframeDisabled(tf)) {
      continue; // skip validation for fully past timeframe
    }

    // Ensure future times for non-edit mode
    if (!isEdit) {
      if (!isNow && startDateTime < now.getTime()) {
        return {
          valid: false,
          index: i,
          error: "Start time must be in the future",
        };
      }

      if (isNow) {
        if (i === 0) {
          const endDateTime = getDateTime(tf.date[1], tf.end_time);
          if (endDateTime <= now.getTime()) {
            return {
              valid: false,
              index: i,
              error: "End time must be in the future",
            };
          }
        } else {
          if (startDateTime < now.getTime()) {
            return {
              valid: false,
              index: i,
              error: "Start time must be in the future",
            };
          }
        }
      }
    }

    // Additional rule for first timeframe only
    if (i === 0) {
      const baselineDate = isEdit && created_at ? new Date(created_at) : now;
      const baselinePlus24 = new Date(
        baselineDate.getTime() + 24 * 60 * 60 * 1000
      );

      // For `NOW` availability: ensure the first timeframe starts within ±5 minutes of now (or `created_at` if editing).
      // This allows a slight buffer(±5mins) to account for client-server time differences and user input delays.
      // if (isNow) {
      // const lowerBound = new Date(baselineDate.getTime() - 5 * 60 * 1000);
      // const upperBound = new Date(baselineDate.getTime() + 5 * 60 * 1000);
      // if (startDateTime < lowerBound.getTime()) {
      //   return {
      //     valid: false,
      //     error: "Start time must be now or later",
      //   };
      // }
      // }

      if (
        isWithin24 &&
        (startDateTime < baselineDate.getTime() ||
          startDateTime > baselinePlus24.getTime())
      ) {
        return {
          valid: false,
          index: i,
          error: "Start time must be within the next 24 hours",
        };
      }

      if (isFutureDate && startDateTime <= baselinePlus24.getTime()) {
        return {
          valid: false,
          index: i,
          error:
            "Start time for future availability must be after 24 hours from now",
        };
      }
    }

    if (isFutureDate && startDateTime <= nowPlus24.getTime()) {
      return {
        valid: false,
        index: i,
        error:
          "Start time for future availability must be after 24 hours from now",
      };
    }

    // Validate overlap with future/ongoing timeframes
    for (let j = i + 1; j < timeframes.length; j++) {
      if (isOverlapping(tf, timeframes[j])) {
        return { valid: false, index: j, error: "Timeframes must not overlap" };
      }
    }
  }

  return { valid: true };
};

// Ensure first timeframe id prefilled with date, start time and next available end time
const prefillTimeframes = (
  timeframes: Timeframe[],
  timeOptions: TimeOption[],
  currentTime: string,
  currentDate: Date,
  isFutureTimelineType?: boolean
): Timeframe[] => {
  const futureDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000); // after 24h

  // Filter available start options for current date
  const filteredStartOptions = filterStartTimeOptions(timeOptions, currentDate);
  const validCurrentTime =
    filteredStartOptions.find((t) => t.value >= currentTime)?.value ||
    currentTime;

  // Find valid end time options for current timeframe
  const validEndOptions = filterEndTimeOptions(
    timeOptions,
    currentDate,
    currentDate,
    validCurrentTime
  );
  // Prefer a 2-hour duration if possible, fallback to 1.5h, 1h, 30 mins, 15 mins
  // With 15-min intervals: index 7 = +2hr, 5 = +1.5hr, 3 = +1hr, 1 = +30min, 0 = +15min
  const defaultEndTime =
    validEndOptions[7]?.value ?? // ~2 hours after start time (8 × 15 min)
    validEndOptions[5]?.value ?? // ~1.5 hours after start time (6 × 15 min)
    validEndOptions[3]?.value ?? // ~1 hour after start time (4 × 15 min)
    validEndOptions[1]?.value ?? // ~30 minutes after start time (2 × 15 min)
    validEndOptions[0]?.value ?? // ~15 minutes after start time (absolute minimum)
    ""; // Fallback if no valid end times available

  // Only update first timeframe with new current values
  return timeframes.map((tf, index) => {
    if (index !== 0) return tf;
    const isToday =
      tf.date &&
      tf.date[0] &&
      new Date(tf.date[0]).toDateString() === currentDate.toDateString();

    return {
      ...tf,
      date: isFutureTimelineType
        ? tf.date === null || isToday
          ? [futureDate, futureDate]
          : tf.date
        : [currentDate, currentDate],
      start_time: validCurrentTime,
      end_time: defaultEndTime,
    };
  });
};

// Clean up: Ensure all timeframes only use valid start/end options
export const updateRangeIfInvalid = (
  timeframes: Timeframe[],
  timeOptions: TimeOption[]
): Timeframe[] => {
  return timeframes.map((tf) => {
    const filteredStartOptions = filterStartTimeOptions(
      timeOptions,
      tf.date?.[0]
    );
    const isStartValid = filteredStartOptions.some(
      (opt) => opt.value === tf.start_time
    );

    const filteredEndOptions = filterEndTimeOptions(
      timeOptions,
      tf.date?.[0],
      tf.date?.[1],
      tf.start_time
    );
    const isEndValid = filteredEndOptions.some(
      (opt) => opt.value === tf.end_time
    );

    return {
      ...tf,
      start_time: isStartValid ? tf.start_time : "",
      end_time: isEndValid ? tf.end_time : "",
    };
  });
};

// Main controller: Enforces NOW logic or general range validation
export const updateTimeframesBasedOnAvailability = (
  timeframes: Timeframe[],
  startAvailability: VACANCY_START_AVAILABILITY,
  timeOptions: TimeOption[],
  localDate: Date // current date
): Timeframe[] => {
  const currentTime = `${localDate
    .getHours()
    .toString()
    .padStart(2, "0")}:${localDate.getMinutes().toString().padStart(2, "0")}`;

  if (startAvailability === VACANCY_START_AVAILABILITY.NOW) {
    return prefillTimeframes(timeframes, timeOptions, currentTime, localDate);
  }

  if (startAvailability === VACANCY_START_AVAILABILITY.WITHIN_24_HOURS) {
    return prefillTimeframes(timeframes, timeOptions, currentTime, localDate);
  }

  if (startAvailability === VACANCY_START_AVAILABILITY.FUTURE_DATE) {
    return prefillTimeframes(
      timeframes,
      timeOptions,
      currentTime,
      localDate,
      true
    );
  }

  // For WITHIN_24_HOURS and FUTURE_DATE, just clean up invalid ranges
  return updateRangeIfInvalid(timeframes, timeOptions);
};

export const checkIfTimeframeInPast = (tf: Timeframe): boolean => {
  if (!tf?.date?.[0] || !tf?.start_time) return false;

  const startDateTime = getDateTime(tf.date[0], tf.start_time);
  const now = new Date().getTime();

  return startDateTime < now;
};
